import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isValidEmail } from '../../utils/validation';

/**
 * EmailInputStep — Email entry with validation.
 * Checks if the email belongs to an existing or new user.
 *
 * @param {Object} props
 * @param {string} props.email - Current email value
 * @param {function} props.onEmailChange - Called with new email value
 * @param {function} props.onContinue - Called with { email, isNewUser }
 * @param {function} props.onBack - Called to go back to method select
 * @param {function} props.checkEmailExists - Async function to check if email exists
 * @returns {JSX.Element}
 */
function EmailInputStep({ email, onEmailChange, onContinue, onBack, checkEmailExists }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const exists = await checkEmailExists(email);
      onContinue({ email, isNewUser: !exists });
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    onEmailChange(e.target.value);
    if (error) setError(null);
  };

  return (
    <div className="auth-step email-input-step">
      <button type="button" className="auth-step__back" onClick={onBack} aria-label="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </button>

      <h2 className="auth-step__title">Enter your email</h2>
      <p className="auth-step__subtitle">We'll check if you already have an account</p>

      <form onSubmit={handleSubmit} className="auth-step__form">
        <div className="auth-input-group">
          <input
            type="email"
            className={`auth-input ${error ? 'auth-input--error' : ''}`}
            placeholder="email@domain.com"
            value={email}
            onChange={handleChange}
            autoFocus
            autoComplete="email"
          />
          {error && <p className="auth-input__error">{error}</p>}
        </div>

        <button
          type="submit"
          className="auth-continue-btn"
          disabled={loading || !email.trim()}
        >
          {loading ? 'Checking...' : 'Continue'}
        </button>
      </form>
    </div>
  );
}

EmailInputStep.propTypes = {
  email: PropTypes.string.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  checkEmailExists: PropTypes.func.isRequired,
};

export default EmailInputStep;
